/**
 * Survey utility functions
 */

interface QuestionConfig {
  id: string;
  type:
    | "text"
    | "number"
    | "radio"
    | "boolean"
    | "card-checkbox"
    | "wildcard"
    | "color-selector";
  optional?: boolean;
  cardCheckboxProps?: {
    singleSelection?: boolean;
  };
}

/**
 * Checks if a survey question is answered based on its type and current value
 * @param question - The question configuration
 * @param value - The current value for the question
 * @returns true if the question has a valid answer, false otherwise
 */
export function isQuestionAnswered(
  question: QuestionConfig,
  value: any
): boolean {
  if (!question) return false;

  switch (question.type) {
    case "text":
      return typeof value === "string" && value.trim() !== "";

    case "number":
      return typeof value === "number" && !isNaN(value);

    case "radio":
    case "boolean":
    case "wildcard":
    case "color-selector":
      return value !== undefined && value !== "";

    case "card-checkbox":
      if (question.cardCheckboxProps?.singleSelection) {
        return typeof value === "string" && value !== "";
      }
      return Array.isArray(value) && value.length > 0;

    default:
      return false;
  }
}

/**
 * Determines the appropriate next button label for a survey question
 * @param question - The question configuration
 * @param value - The current value for the question
 * @param isLastQuestion - Whether this is the last question in the survey
 * @returns The appropriate button label ('Skip', 'Continue', or 'Complete')
 */
export function getSurveyButtonLabel(
  question: QuestionConfig,
  value: any,
  isLastQuestion: boolean
): string {
  if (isLastQuestion) {
    return "Complete";
  }

  const isAnswered = isQuestionAnswered(question, value);
  const isOptional = question.optional === true;

  if (isOptional && !isAnswered) {
    return "Skip";
  }

  return "Continue";
}

/**
 * Determines if the survey next button should be disabled
 * @param question - The question configuration
 * @param value - The current value for the question
 * @returns true if the button should be disabled, false otherwise
 */
export function isSurveyButtonDisabled(
  question: QuestionConfig,
  value: any
): boolean {
  const isAnswered = isQuestionAnswered(question, value);
  const isOptional = question.optional === true;

  return !isAnswered && !isOptional;
}
